// 騎士の固有のとどめ (突進) と弾かれた花嫁の音 (2026-10-12・音の専用セッション)。
//   ユーザー原文 (10-12)「音のセッションは別で作って進められるものがあるなら別セッション立てて進めたい」
//   「進められる音作りは自立してガンガン進めてほしい」。
//   どれもボスの体と得物が立てる音 (露骨な合図の層は足さない)。名前が knight_exec_ / bride_parried_ で始まる = ボスの技の音
//   (src/sound_priority.ts: プレイヤーの弾き・被弾が鳴ると −9dB 下がる)。当たった・弾いた瞬間はプレイヤーの音 (hit_player・parry) が主役のまま。
//   素材 = scripts/render_exec_unique_sfx.py (ElevenLabs のテイクを数値で選んで整えた)。記録 = docs/research/2026-10-12-exec-unique-sfx.md。
//
//   既定 a = 本命 (配布版)。開発版 ?execUniqueSound=b = 別テイク (public/sfx/candidates/・git 外)・=old = 前の音 (この音を足さない)。
//   URL は game.ts の DEV の箱の中でだけ読む。
import type { Boss, SfxId } from './types'
import { KNIGHT_WALL_PASS, knightChargeRunImageAt, knightPassRunImageAt, BRIDE_PARRIED_SNAP, execUniqueBrideRise } from './exec_unique'

export type ExecUniqueSound = 'a' | 'b' | 'old'
let take: ExecUniqueSound = 'a'
export function setExecUniqueSound(value: string | null | undefined): ExecUniqueSound {
  take = value === 'b' || value === 'old' ? value : 'a'
  // 別テイクは開発版でこの時だけ読む (audio.ts は重いので後から読む)。
  if (take === 'b' && typeof fetch !== 'undefined') void import('./audio').then(m => m.preloadExecUniqueSoundB())
  return take
}
export function execUniqueSoundTake(): ExecUniqueSound { return take }

export const EXEC_UNIQUE_SOUND_ROLES = ['knight_walk', 'knight_settle', 'knight_kick', 'knight_stride', 'knight_rush',
  'knight_impact', 'knight_brake', 'knight_recoil', 'knight_scrape', 'bride_snap', 'bride_settle'] as const
export type ExecUniqueSoundRole = typeof EXEC_UNIQUE_SOUND_ROLES[number]
/** 役 → 効果音の名前 (knight_exec_* / bride_parried_*)。 */
export function execUniqueSfxId(role: ExecUniqueSoundRole): SfxId {
  return (role.startsWith('knight_') ? `knight_exec_${role.slice(7)}` : `bride_parried_${role.slice(6)}`) as SfxId
}
/** 先読みする素材 [キー, URL]。キー = 効果音の名前。 */
export function execUniqueSoundFiles(which: 'a' | 'b'): [string, string][] {
  return EXEC_UNIQUE_SOUND_ROLES.map(role => [execUniqueSfxId(role),
    which === 'a' ? `/sfx/exec_unique_${role}.wav?v=1` : `/sfx/candidates/exec_unique_${role}_b.wav?v=1`])
}

/** 当たってからこの時刻 (active の頭から ms) で滑る音を鳴らす (弾かれたかどうかが決まった後)。 */
export const KNIGHT_BRAKE_SOUND_AT_MS = 10
const crossed = (a: number, b: number, at: number) => a < at && b >= at

/** この tick で鳴らす音 (prev = 前の tick のボス・next = 当たり・弾きを解いた後のボス)。 */
export function execUniqueSfx(prev: Boss, next: Boss): SfxId[] {
  if (take === 'old') return []
  const plan = next.currentAttack?.uniqueExec
  if (!plan || prev.currentAttack?.uniqueExec?.boss !== plan.boss) {
    return []
  }
  const out: SfxId[] = []
  const same = prev.state === next.state
  const a = prev.stateElapsed * 1000
  const b = next.stateElapsed * 1000
  // 弾かれた瞬間 (hits.ts が印を立てた tick): 騎士はのけぞる鎧と槍の柄・花嫁はあおられるヴェールと薔薇の鎖。
  const parriedNow = !!plan.parried && !prev.currentAttack?.uniqueExec?.parried
  if (plan.boss === 'vigil-knight') {
    if (parriedNow) out.push('knight_exec_recoil')
    if (plan.form !== 'charge') return out
    if (next.state === 'telegraph' && same) {
      for (const s of plan.steps) if (crossed(a, b, s.startMs + (s.carryEndMs - s.startMs) * 0.85)) out.push('knight_exec_walk')
      if (crossed(a, b, plan.holdStartMs)) out.push('knight_exec_settle')
      // 壁ぎわの駆け抜け: 蹴り出し・一歩ごと・踏ん張って床を擦る (走りの絵が消えた所 = 踏ん張りの絵)。
      const pa = knightPassRunImageAt(plan, a), pb = knightPassRunImageAt(plan, b)
      if (pb && !pa) out.push('knight_exec_kick')
      else if (pb && pa !== pb) out.push('knight_exec_stride')
      if (plan.pass && pa && !pb && b < plan.pass.endMs) out.push('knight_exec_scrape')
      // 突進: 走り出しで後ろ足の蹴り出しと風 (当たる所へ向けて膨らむ)・走りの絵が替わるたびに重い一歩。
      const ra = knightChargeRunImageAt(plan, a), rb = knightChargeRunImageAt(plan, b)
      if (rb && !ra) out.push('knight_exec_kick', 'knight_exec_rush')
      else if (rb && ra !== rb) out.push('knight_exec_stride')
    }
    // 当たった瞬間: 騎士の体の衝撃 (いちばん重い・10-12「火力や行動の重さに比例して重さを」)。弾かれた時はのけぞる音だけ。
    if (next.state === 'active' && prev.state === 'telegraph' && !plan.parried) out.push('knight_exec_impact')
    // 当たった: 勢いで滑って止まる (弾かれた時は鳴らさない)。当たり・弾きが解けた後 (当たりの止めが明けて滑り出す所) で鳴らす。
    //   当たりに入った最初の tick で、もう 10ms を越えていることがある (前の tick の端数) ので、溜めから来た時は -1 から数える。
    const aActive = prev.state === 'active' ? a : prev.state === 'telegraph' ? -1 : Infinity
    if (next.state === 'active' && !plan.parried && crossed(aActive, b, KNIGHT_BRAKE_SOUND_AT_MS)) out.push('knight_exec_brake')
    return out
  }
  if (parriedNow && execUniqueBrideRise() === 'snap') out.push('bride_parried_snap')
  // 弾かれた花嫁が待機の絵へ戻る (snap の riseAt): 衣が落ちて据わる。
  if (plan.parried && execUniqueBrideRise() === 'snap' && next.state === 'recovery' && same) {
    const at = next.currentAttack!.recoveryMs * BRIDE_PARRIED_SNAP.riseAt
    if (crossed(a, b, at)) out.push('bride_parried_settle')
  }
  return out
}

/** 踏ん張りの割合 (記録用・駆け抜けの進んだ距離のこの割合から床を擦る)。 */
export const EXEC_UNIQUE_SCRAPE_SHARE = KNIGHT_WALL_PASS.brakeShare
